import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useAction } from 'wasp/client/operations';
import { getLocationById, deleteLocation, getAllLocations, getLocationTypes } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { isAdministrator } from '../../client/lib/platformRole';
import {
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  ChevronUp,
  ChevronDown,
  Globe,
  Users
} from 'lucide-react';
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButtonLink,
  ActionButtonDanger
} from '../../components/layout';
import { toast } from '../../hooks/use-toast';
import { type Location } from 'wasp/entities';

export default function ViewLocationPage() {
  const { id } = useParams<{ id: string }>();
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const deleteLocationFn = useAction(deleteLocation);

  // Fetch location data
  const {
    data: location,
    isLoading,
    error,
    refetch
  } = useQuery(getLocationById, { id: id! }, { enabled: !!id });

  // Fetch all locations for navigation
  const { data: allLocationsData } = useQuery(getAllLocations, { limit: 100 });
  const allLocations = (allLocationsData && typeof allLocationsData === 'object' && 'locations' in allLocationsData) 
    ? (allLocationsData as any).locations 
    : [];

  // Fetch location types for displaying type label
  const { data: locationTypes = [] } = useQuery(getLocationTypes);

  const formatDate = (dateInput: string | Date) => {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleBack = () => {
    navigate('/admin/taxonomy/location-names');
  };

  // Find current index and get prev/next locations
  const currentIndex = allLocations.findIndex((loc: any) => loc.id === id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < allLocations.length - 1;
  const prevLocation = hasPrev ? allLocations[currentIndex - 1] : null;
  const nextLocation = hasNext ? allLocations[currentIndex + 1] : null;

  const handleDelete = async () => {
    if (!location) return;
    if (!confirm(`Are you sure you want to delete "${(location as any).name}"?`)) return;

    try {
      await deleteLocationFn({ id: (location as any).id });
      toast({
        title: "Success",
        description: "Location deleted successfully"
      });
      navigate('/admin/taxonomy/location-names');
    } catch (err: any) {
      console.error('Error deleting location:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to delete location",
        variant: "destructive"
      });
    }
  };

  if (!user || !isAdministrator(user)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">Admin privileges required.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading location...</p>
        </div>
      </div>
    );
  }

  if (error || !location) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <MapPin className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Location Not Found</h3>
          <p className="text-gray-600 mb-4">The location you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Locations
          </button>
        </div>
      </div>
    );
  }

  const locationTypeName = (location as any).locationTypeId 
    ? (locationTypes as any[]).find((lt: any) => lt.id === (location as any).locationTypeId)?.label 
    : undefined;

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <div className="space-y-3">
          {/* Navigation Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => prevLocation && navigate(`/admin/taxonomy/location-names/${prevLocation.id}`)}
              disabled={!hasPrev}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              title={hasPrev ? `Previous: ${prevLocation?.name}` : 'No previous item'}
            >
              <ChevronUp className="w-4 h-4" />
              <span className="text-sm font-medium">Up</span>
            </button>
            <button
              onClick={() => nextLocation && navigate(`/admin/taxonomy/location-names/${nextLocation.id}`)}
              disabled={!hasNext}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              title={hasNext ? `Next: ${nextLocation?.name}` : 'No next item'}
            >
              <span className="text-sm font-medium">Down</span>
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>

          {/* Action Buttons */}
          <ActionButtons>
            <ActionButtonLink
              to={`/admin/taxonomy/location-names/${(location as any).id}/edit`}
            >
              Edit
            </ActionButtonLink>
            <ActionButtonDanger
              onClick={handleDelete}
            >
              Delete
            </ActionButtonDanger>
          </ActionButtons>
        </div>
      </SidebarSection>

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField label="Code" value={(location as any).locationCode} />
          <DetailField label="Type" value={locationTypeName || 'None'} />
          <DetailField label="Public" value={(location as any).isPublic ? 'Yes' : 'No'} />
          <DetailField label="Public Subscription" value={(location as any).allowPublicSubscription ? 'Allowed' : 'Not Allowed'} />
          <DetailField label="Default Visibility" value={(location as any).defaultVisibility} />
          <DetailField label="Timezone" value={(location as any).timezone || 'UTC'} />
          <DetailField label="Created" value={formatDate((location as any).createdAt)} />
          <DetailField label="Last Updated" value={formatDate((location as any).updatedAt)} />
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={[
        { label: 'Admin', href: '/admin' },
        { label: 'Locations', href: '/admin/taxonomy/location-names', isClickable: true }
      ]}
      currentPageUrl={typeof window !== 'undefined' ? window.location.href : ''}
      sidebarContent={sidebarContent}
      layoutVariant="taxonomy"
      authUser={user}
    >
      {/* Location Header */}
      <div className="mb-8">
        {/* Title and Status */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{(location as any).name}</h1>
            <p className="text-lg text-gray-600 font-mono">{(location as any).locationCode}</p>
          </div>

          {/* Status Badge */}
          <div className="flex items-center gap-2 ml-4">
            {(location as any).isPublic ? (
              <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-blue-100 text-blue-800">
                <Globe className="w-4 h-4" />
                <span className="font-medium">Public</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-gray-100 text-gray-800">
                <Users className="w-4 h-4" />
                <span className="font-medium">Private</span>
              </div>
            )}
          </div>
        </div>

        {/* Meta Information */}
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 border-b border-gray-200 pb-6">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Created {formatDate((location as any).createdAt)}</span>
          </div>

          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>Updated {formatDate((location as any).updatedAt)}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-8">
        {/* Basic Information */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Basic Information</h2>
          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-600 font-medium mb-2">Location Name</p>
                <p className="text-gray-900">{(location as any).name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium mb-2">Location Type</p>
                <p className="text-gray-900">{locationTypeName || 'None'}</p>
              </div>
            </div>
            {(location as any).publicDescription && (
              <div>
                <p className="text-sm text-gray-600 font-medium mb-2">Public Description</p>
                <p className="text-gray-900">{(location as any).publicDescription}</p>
              </div>
            )}
          </div>
        </div>

        {/* Settings */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Settings</h2>
          <div className="bg-gray-50 rounded-lg p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-600 font-medium mb-2">Public Location</p>
                <p className="text-gray-900">{(location as any).isPublic ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium mb-2">Public Subscription</p>
                <p className="text-gray-900">{(location as any).allowPublicSubscription ? 'Allowed' : 'Not Allowed'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium mb-2">Default Content Visibility</p>
                <p className="text-gray-900">{(location as any).defaultVisibility}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Timezone */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Timezone</h2>
          <div className="bg-gray-50 rounded-lg p-6">
            <p className="text-sm text-gray-600 font-medium mb-2">Timezone</p>
            <p className="text-gray-900">{(location as any).timezone || 'UTC'}</p>
          </div>
        </div>

        {/* Hub/Hierarchy Information */}
        {((location as any).isHub || (location as any).parentLocationId) && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">Hub & Hierarchy</h2>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 font-medium mb-2">Is Hub</p>
                  <p className="text-gray-900">{(location as any).isHub ? 'Yes' : 'No'}</p>
                </div>
                {(location as any).isHub && (
                  <div>
                    <p className="text-sm text-gray-600 font-medium mb-2">Aggregate Child Content</p>
                    <p className="text-gray-900">{(location as any).aggregateChildContent ? 'Yes' : 'No'}</p>
                  </div>
                )}
              </div>
              {(location as any).parentLocationId && (
                <div>
                  <p className="text-sm text-gray-600 font-medium mb-2">Parent Location</p>
                  <p className="text-gray-900">{(location as any).parentLocationId}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </IndividualViewPageLayout>
  );
}



